import type { DeviceConnectivity } from "@led-signage/shared/device-api";
import type {
  AuditLogRecord,
  PublicationPreparation,
  DisplayDetail,
  MediaAsset,
  PublicationRequest,
} from "@led-signage/shared/management-api";

/**
 * 契約型を画面表示へ変換する。判定条件は仕様側に根拠があるものだけを置き、
 * 画面ごとに解釈がぶれないようにする。
 */

/**
 * 未送信eventがこの件数に達したら警告する。
 *
 * 端末側の上限50,000件（[Issue #117](https://github.com/YuYuAoi/led-signage-platform/issues/117)）
 * の80%。ここを超えると端末が古い実績の再集約と破棄を始めるため、**破棄が
 * 起きる前に人が気づける位置**に置く。
 */
const EVENT_QUEUE_WARNING_DEPTH = 40_000;

/** docs/api/device-api.md: 絶対値30秒以上のclock offsetを警告する */
export const CLOCK_OFFSET_WARNING_MS = 30_000;

export function mediaStatusLabel(status: MediaAsset["status"]): string {
  const labels: Record<MediaAsset["status"], string> = {
    uploading: "アップロード中",
    uploaded: "検査待ち",
    validating: "検査中",
    ready: "利用可能",
    rejected: "非対応",
    archived: "アーカイブ済み",
  };
  return labels[status];
}

export function mediaStatusTone(status: MediaAsset["status"]): string {
  if (status === "ready") return "ok";
  if (status === "rejected") return "bad";
  if (status === "archived") return "muted";
  return "pending";
}

export function durationLabel(durationMs: number | null): string {
  if (durationMs === null) return "—";
  const total = Math.round(durationMs / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function resolutionLabel(asset: MediaAsset): string {
  if (asset.width === null || asset.height === null) return "解像度未確定";
  return `${String(asset.width)}×${String(asset.height)}`;
}

export function byteLabel(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(0)} MB`;
  return `${(bytes / 1000).toFixed(0)} KB`;
}

export interface DisplayView {
  id: string;
  name: string;
  /** online / warning / offline。画面の既存クラス名に合わせる */
  status: "online" | "warning" | "offline";
  playing: string;
  sync: string;
  /** 運用者が対処すべき事象。空なら正常 */
  alerts: readonly string[];
}

export function displayView(
  detail: DisplayDetail,
  assetName: (id: string | null) => string,
): DisplayView {
  const status = detail.deviceStatus;
  const alerts: string[] = [];

  if (detail.device === null) {
    return {
      id: detail.display.id,
      name: detail.display.name,
      status: "offline",
      playing: "—",
      sync: "端末未登録",
      alerts: ["再生端末が登録されていません"],
    };
  }
  if (status === null) {
    return {
      id: detail.display.id,
      name: detail.display.name,
      status: "offline",
      playing: "—",
      sync: "未接続",
      alerts: ["端末から一度も状態が届いていません"],
    };
  }

  const distributing =
    status.desiredReleaseId !== null &&
    status.currentReleaseId !== status.desiredReleaseId;

  const clockOffsetMs = status.clockOffsetMs;
  if (clockOffsetMs !== null && Math.abs(clockOffsetMs) >= CLOCK_OFFSET_WARNING_MS) {
    alerts.push(
      `端末の時刻が${String(Math.round(clockOffsetMs / 1000))}秒ずれています`,
    );
  }
  if (status.playbackState === "fallback") {
    alerts.push("デフォルト動画を再生中です");
  }
  if (status.lastErrorCode !== null) {
    alerts.push(status.lastErrorMessage ?? status.lastErrorCode);
  }
  // 破棄が起きた端末は放映実績が欠けている。件数より「不完全である」ことを言う。
  if (status.eventQueueDroppedCount !== null && status.eventQueueDroppedCount > 0) {
    alerts.push(
      `再生実績が${String(status.eventQueueDroppedCount)}件失われています。この端末の放映回数は実際より少なくなります`,
    );
  }
  if (
    status.eventQueueDepth !== null &&
    status.eventQueueDepth >= EVENT_QUEUE_WARNING_DEPTH
  ) {
    alerts.push(
      `未送信の再生実績が${String(status.eventQueueDepth)}件溜まっています。通信を確認してください`,
    );
  }

  const connectivity: DisplayView["status"] =
    status.connectivity === "online"
      ? distributing || alerts.length > 0
        ? "warning"
        : "online"
      : "offline";

  return {
    id: detail.display.id,
    name: detail.display.name,
    status: connectivity,
    playing:
      status.playbackState === "playing"
        ? assetName(status.currentMediaAssetId)
        : status.playbackState === "fallback"
          ? "デフォルト動画"
          : "停止中",
    sync: distributing ? "配信中" : "同期済み",
    alerts,
  };
}

/**
 * 警告の重さ。並べる順序と見た目を決める。
 *
 * すべて同じ赤いタグで並べると、いちばん重いものが埋もれる。承認者が
 * 3秒で「これは止まるべきか」を判断できることを優先する。
 */
export type WarningSeverity = "critical" | "high" | "medium";

export interface ApprovalWarning {
  code: string;
  label: string;
  /** なぜ注意が要るのか。ラベルだけでは判断できないため添える。 */
  detail: string;
  severity: WarningSeverity;
}

const SEVERITY_ORDER: Readonly<Record<WarningSeverity, number>> = {
  critical: 0,
  high: 1,
  medium: 2,
};

export interface ApprovalView {
  id: string;
  title: string;
  /** 承認前に必ず目に入れるべき警告。重い順。 */
  warnings: readonly ApprovalWarning[];
  blocking: readonly string[];
  advisory: readonly string[];
  displayCount: number;
}

export function approvalWarnings(
  request: PublicationRequest,
): ApprovalWarning[] {
  const warnings: ApprovalWarning[] = [];

  if (request.emergencyOverride) {
    warnings.push({
      code: "EMERGENCY_OVERRIDE",
      label: "緊急配信（承認省略）",
      // 通常の公開と違い、内容を確認した第三者が存在しない。
      detail:
        "この依頼は承認を省略しています。内容を確認した別の担当者がいません。",
      severity: "critical",
    });
  }

  if (request.sameDayChange) {
    warnings.push({
      code: "SAME_DAY_CHANGE",
      label: "当日変更",
      // 公開後に問題が見つかっても、差し戻して直す時間がない。
      detail:
        "本日分の放映を変更します。問題が見つかっても手直しする時間がありません。",
      severity: "high",
    });
  }

  if (request.displayIds.length > 1) {
    warnings.push({
      code: "MULTIPLE_DISPLAYS",
      label: `${String(request.displayIds.length)}台へ同時公開`,
      detail: "誤りがあると、複数の拠点へ同時に反映されます。",
      severity: "medium",
    });
  }

  return warnings.sort(
    (left, right) =>
      SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity],
  );
}

export function approvalView(request: PublicationRequest): ApprovalView {
  return {
    warnings: approvalWarnings(request),
    id: request.id,
    title: request.changeReason,
    blocking: request.validation.errors.map((issue) =>
      issueLabel(issue.code, issue.message),
    ),
    advisory: request.validation.warnings.map((issue) =>
      issueLabel(issue.code, issue.message),
    ),
    displayCount: request.displayIds.length,
  };
}

export interface AuditLogView {
  id: string;
  date: string;
  time: string;
  actor: string;
  action: string;
  target: string;
  outcome: "success" | "failure";
  outcomeLabel: string;
  detail: string | null;
}

const ACTION_LABELS: Readonly<Record<string, string>> = {
  "media.upload.complete": "動画をアップロード",
  "publication_request.approve": "公開を承認",
  "release.activate": "配信版を適用",
  "authorization.denied": "権限不足で操作を拒否",
};

const TARGET_LABELS: Readonly<Record<string, string>> = {
  media_asset: "動画",
  publication_request: "公開申請",
  release: "配信版",
  permission: "権限",
};

function actorLabel(record: AuditLogRecord): string {
  if (record.actorType === "system") return "システム";
  if (record.actorName !== null) return record.actorName;
  if (record.actorType === "device") {
    return record.actorId === null ? "再生端末" : `端末 ${record.actorId}`;
  }
  return record.actorId === null ? "利用者" : `利用者 ${record.actorId}`;
}

export function auditLogView(record: AuditLogRecord): AuditLogView {
  const timestamp = new Date(record.occurredAt);
  const date = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(timestamp);
  const time = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(timestamp);
  const targetType = TARGET_LABELS[record.targetType] ?? record.targetType;
  const target = record.targetId === null
    ? targetType
    : `${targetType} / ${record.targetId}`;
  return {
    id: record.id,
    date,
    time,
    actor: actorLabel(record),
    action: ACTION_LABELS[record.action] ?? record.action,
    target,
    outcome: record.outcome,
    outcomeLabel: record.outcome === "success" ? "成功" : "失敗",
    detail: record.errorMessage ?? record.reason,
  };
}

/**
 * 公開前検査のコードを日本語へ。
 *
 * サーバーは機械判定用の`code`と英語の`message`を返す。画面はコードで分岐し、
 * 未知のコードだけサーバーの文言へ落とす。**コードを知らないからといって
 * 黙って隠さない。**承認者が見落とす方が危険なため。
 */
const ISSUE_LABELS: Readonly<Record<string, string>> = {
  // スケジュール・プレイリスト
  PLAYLIST_EMPTY: "プレイリストに動画が1本も入っていません",
  PLAYLIST_MEDIA_NOT_READY: "プレイリスト内に、まだ配信できない動画があります",
  PLAYLIST_UNAVAILABLE: "指定されたプレイリストを参照できません",
  SCHEDULE_UNAVAILABLE: "指定されたスケジュールを参照できません",
  SCHEDULE_NO_TARGETS: "対象のLEDが指定されていません",
  SCHEDULE_CONFLICT: "同じ優先度の編成と時間帯が重なっています",
  SCHEDULE_PRIORITY_OVERLAP:
    "優先度の異なる編成と重なっています。高いほうが再生されます",
  // 動画
  MEDIA_NOT_READY: "動画の形式検査が終わっていません",
  MEDIA_ASSETS_NOT_READY: "必要な動画が端末へ配信できる状態になっていません",
  MEDIA_HAS_NO_AUDIO: "音声を有効にしていますが、動画に音声トラックがありません",
  // 端末
  DEVICE_NOT_ENROLLED: "対象のLEDに再生端末が登録されていません",
  DEVICE_OFFLINE: "再生端末がオフラインです",
  DEVICE_CONNECTIVITY_UNKNOWN: "再生端末からまだ一度も報告がありません",
  DEVICE_STORAGE_UNKNOWN: "再生端末の空き容量が分かりません",
  INSUFFICIENT_DEVICE_STORAGE: "再生端末の空き容量が足りません",
  DISPLAY_UNAVAILABLE: "対象のLEDを参照できません",
  // 手続き
  PUBLICATION_VALIDATION_FAILED: "公開前検査でエラーが出ています",
  SAME_DAY_CHANGE_CONFIRMATION_REQUIRED: "当日変更の確認が必要です",
};

export function issueLabel(code: string, fallback: string): string {
  return ISSUE_LABELS[code] ?? fallback;
}

export interface TargetPreparationView {
  displayId: string;
  /** 端末が居ないこともある */
  deviceId: string | null;
  connectivityLabel: string;
  stateLabel: string;
  ready: boolean;
  issues: readonly string[];
}

export interface PreparationView {
  stateLabel: string;
  ready: boolean;
  assetProgress: string;
  targets: readonly TargetPreparationView[];
}

const CONNECTIVITY_LABELS: Readonly<Record<DeviceConnectivity, string>> = {
  online: "稼働中",
  offline: "オフライン",
  // 「一度も報告がない」と「途絶えた」は別の状態として扱う。
  unknown: "未接続",
};

const PREPARATION_STATE_LABELS: Readonly<
  Record<PublicationPreparation["state"], string>
> = {
  ready: "配信できます",
  pending: "準備中です",
  blocked: "このままでは配信できません",
};

export function preparationView(
  preparation: PublicationPreparation,
): PreparationView {
  return {
    stateLabel: PREPARATION_STATE_LABELS[preparation.state],
    ready: preparation.state === "ready",
    assetProgress: `${String(preparation.readyAssetCount)} / ${String(preparation.totalAssetCount)} 本`,
    targets: preparation.targets.map((target) => ({
      displayId: target.displayId,
      deviceId: target.deviceId,
      connectivityLabel: CONNECTIVITY_LABELS[target.connectivity],
      stateLabel: PREPARATION_STATE_LABELS[target.state],
      ready: target.state === "ready",
      issues: target.issues.map((code) => issueLabel(code, code)),
    })),
  };
}
